import {Trans} from '@lingui/macro';
import {flow, partition} from 'lodash';
import memoize from 'lru-memoize';
import Token from 'markdown-it/lib/token';
import {transparentize} from 'polished';
import React, {FC, memo, ReactNode, useCallback, useMemo} from 'react';
import styled from 'styled-components';

import {directHoverSelector} from '../../../../../../libs/shared/helpers/src/browser/hoverHelpers';
import {tryParseUrl} from '../../../../../../libs/shared/helpers/src/browser/urlHelpers';
import {Handler, HandlerOf, RendererOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {useInteractionContext} from '../../../../../../libs/shared/ui/src/interactionTracking/interactionContext';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {basicMarkdownStyle} from '../../../../../../libs/shared/ui/src/styles/markdownStyles';
import {textSelection} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {MarkdownFormatter} from '../../../../../../srcCommon/formatters/markdown/markdownFormatter';
import {
  findSingleImageAttachment,
  isCommentOnlyAnEmoji,
  shouldUseMultiAttachmentStyle,
} from '../../../../../helpers/commentViewerHelpers';
import {isHighlightComment} from '../../../../../helpers/models/commentHelpers';
import {useAppSelector} from '../../../../../helpers/reduxHelpers';
import {
  AnnotationHighlightCommentModel,
  RHighlightCommentAnnotationDirty,
} from '../../../../../models/api/annotationModel';
import {
  AttachmentModel,
  AttachmentStatusesEnum,
  AttachmentVisualContentTypesEnum,
  UploadedAttachmentModel,
} from '../../../../../models/api/attachmentModel';
import {
  CommentModel,
  RAnnotationComment,
  RHighlightMessageAnnotationComment,
} from '../../../../../models/api/commentModel';
import {CommentUnfurlModel} from '../../../../../models/api/commentUnfurlModel';
import {TeammateModel} from '../../../../../models/api/teammateModel';
import {VisualRecipientModel} from '../../../../../models/api/visualRecipientModel';
import {EmojiMap} from '../../../../../services/emojis/emojis';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../../services/tracking';
import {getHasP3LinkTracking} from '../../../../../store/featureFlags/featureFlagsSelectors';
import {NewPanelId} from '../../../../../store/panels/panelStateBase';
import {getEmojiValidationMap} from '../../../../../store/stateless/emojiSelectors';
import {AnnotationSummaryContainer} from '../../../../common/annotation/annotationSummaryContainer';
import {MarkdownTokenFormatter} from '../../../../common/formatters/markdown/markdownTokenFormatter';
import {makeMarkdownTransformEmojis} from '../../../../common/formatters/markdown/markdownTransformEmojis';
import {makeMarkdownTransformMentions} from '../../../../common/formatters/markdown/markdownTransformMentions';
import {makeMarkdownTransformUnfurls} from '../../../../common/formatters/markdown/markdownTransformUnfurls';
import {useLinkNavigation} from '../../../../common/navigation/useLinkNavigation';
import {CommentSummary} from '../../../../common/referencedActivity/commentSummary';
import {CommentViewerAttachments} from './commentViewerAttachments';
import {CommentViewerSingleImageAttachmentContainer} from './commentViewerSingleImageAttachmentContainer';
import {CommentViewerUnfurls} from './commentViewerUnfurls';

/*
 * Props + Types.
 */

export interface CommentBodyProps {
  panelId: NewPanelId;
  comment: CommentModel;
  referencedAuthor?: TeammateModel;
  referencedComment?: CommentModel;
  referencedRecipient?: VisualRecipientModel;
  allAliases: ReadonlySet<string>;
  mentionableAliases: ReadonlySet<string>;
  userAlias?: string;
  attachments: ReadonlyArray<AttachmentModel>;
  shouldAlwaysUseMultiAttachmentStyle?: boolean;
  shouldHideAnnotation?: boolean;
  onReferencedActivityClick?: HandlerOf<number>;
  renderOneBigEmoji?: RendererOf<ReactNode>;
  renderCommentMarkdown?: RendererOf<CommentMarkdownRendererProps>;
  renderImageAttachments?: RendererOf<ReactNode>;
  isReferencingTheSameMessageAsPrevious?: boolean;

  // The only UI Interaction for Conversation Summary's pinned comments is to
  // jump to the comment in the timeline in P3. Thus this flag disables all UI and interaction around:
  //    - Open conversation preview in a popout window.
  //    - Downloading an image
  //    - Navigating to a referenced comment or message.
  isInteractionDisabled?: boolean;

  // Whether the margin of the comment bubble is disabled. This is used for pinned comments.
  isCommentMarginDisabled?: boolean;

  shouldTruncateAnnotationText?: boolean;
  namespace: string | undefined;
  hasQuoteComment: boolean;
  textWrapper?: (child: ReactNode) => ReactNode;
  isJumpPointerDisabled?: boolean;
}

/*
 * Style.
 */

const StyledMarkdownDiv = styled.div`
  ${basicMarkdownStyle()};

  *,
  & {
    word-wrap: break-word;
  }

  p {
    margin: 0;
  }

  blockquote {
    padding-left: 10px;
    margin: 0.5em 0;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 4px;
      height: 100%;
      background-color: ${(p) => p.theme.greys.shade50};
      border-radius: 2px;
    }
  }

  ol,
  ul {
    margin: 0;
    padding-left: 1.5em;
  }

  li {
    margin: 0;
  }

  pre {
    max-width: 80ch;
    margin: 0.5em 0;
    white-space: pre-wrap;

    code {
      display: block;
      padding: 2px 6px;
      margin: 0;
    }
  }

  * + p,
  * + ul,
  * + ol,
  * + pre {
    margin-top: 1em;
  }

  img.front-markdown-image {
    max-width: 200px;
    max-height: 200px;
    border-radius: 10px;
  }
`;

export const StyledAttachmentMarginTopDiv = styled.div`
  margin-top: 8px;
`;

interface SingleImageDivProps {
  $isReference: boolean;
}

export const StyledSingleImageDiv = styled.div<SingleImageDivProps>`
  display: flex;
  padding: ${(p) => (p.$isReference ? '10px 12px 12px' : '0px')};
`;

export interface StyledCommentDivProps {
  $newMultiAttachmentStyle?: boolean;
  $isReply?: boolean;
}

export const StyledCommentDiv = styled(StyledMarkdownDiv)<StyledCommentDivProps>`
  ${textSelection()};
  padding: ${(p) => getCommentDivPadding(p)};
  line-height: 22px;
`;

function getCommentDivPadding(props: StyledCommentDivProps) {
  if (props.$newMultiAttachmentStyle) {
    return '4px 12px';
  }

  if (props.$isReply) {
    return '0px 10px 4px';
  }

  return '4px 10px 4px';
}

export const StyledReferencedCommentSummaryButton = styled(ChromelessButton)`
  padding: 4px 6px 2px;
  border-bottom: 1px solid ${(p) => p.theme.greys.shade40};
`;

export const StyledAnnotationSummaryButton = styled(ChromelessButton)`
  padding: 4px 6px 2px;
  border-bottom: 1px solid ${(p) => p.theme.greys.shade40};
  margin: 9px 9px 1px 9px;
  border-radius: 12px;
  background-color: ${(p) => p.theme.greys.white};
  border: 1px solid ${(p) => p.theme.greys.shade40};
  position: relative;
  z-index: 0;
`;

// Empty by default, but meant to be overridden for custom styles.
export const StyledCommentViewerAttachmentsContainer = styled.div``;

export const StyledEmojiDiv = styled(StyledMarkdownDiv)`
  img {
    width: 32px;
    height: 32px;
    margin-bottom: 2px;
    margin-top: -1px;
  }
`;

const StyledReferencedCommentSummaryButtonHover = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 14px;

  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: center;
  background: linear-gradient(
    180deg,
    ${(p) => transparentize(0.4, p.theme.greys.white)} 0%,
    ${(p) => p.theme.greys.white} 100%
  );
  z-index: 1;
  opacity: 0;
  transition: opacity 300ms ease;

  ${directHoverSelector} {
    opacity: 1;
  }
`;

const StyledCommentBodyHoverLabel = styled.div`
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;

  color: ${(p) => p.theme.palette.blue.shade40};
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.verySmall};

  svg {
    transform: rotate(180deg);
  }
`;

/*
 * Component.
 */

export const CommentBody: FC<CommentBodyProps> = memo((props: CommentBodyProps) => {
  const {
    comment,
    attachments,
    panelId,
    referencedAuthor,
    allAliases,
    referencedComment,
    mentionableAliases,
    userAlias,
    shouldAlwaysUseMultiAttachmentStyle,
    shouldHideAnnotation,
    renderOneBigEmoji,
    renderCommentMarkdown,
    isInteractionDisabled,
    isCommentMarginDisabled,
    namespace,
    hasQuoteComment,
    textWrapper,
    isJumpPointerDisabled,
  } = props;
  const {navigateToLink, maybeRenderLinkError} = useLinkNavigation();

  const hasP3LinkTracking = useAppSelector(getHasP3LinkTracking);
  const onTrackLink = (href: string) => {
    const url = tryParseUrl(href);
    if (!url) {
      return;
    }
    trackInterfaceEvent(InterfaceEventsEnum.P3_COMMENT_LINK_CLICK, url.hostname);
  };

  const onNavigateToLink = (link: string, shiftKey?: boolean) => {
    if (hasP3LinkTracking) {
      onTrackLink(link);
    }
    navigateToLink(link, shiftKey);
  };

  const emojiValidationMap = useAppSelector(getEmojiValidationMap);
  const newMultiAttachmentStyle =
    shouldUseMultiAttachmentStyle(comment) || Boolean(shouldAlwaysUseMultiAttachmentStyle);
  const singleImageAttachment = findSingleImageAttachment(comment);
  const isReply = Boolean(referencedAuthor);
  const {parentInteractionIds} = useInteractionContext();
  const isInConversationSummary = useMemo(
    () => parentInteractionIds.includes('conversation-summary'),
    [parentInteractionIds],
  );

  const onReferencedActivityClick = useCallback(
    (referencedActivityId: number) => {
      props.onReferencedActivityClick?.(referencedActivityId);

      trackInterfaceEvent(InterfaceEventsEnum.P3_MESSAGE_COMMENT_CLICK, {
        namespace,
        conversation_id: comment.conversationId,
        cta_type: 'jump_to_comment',
        location: isInConversationSummary ? 'details' : 'p3_window',
        has_comment_preview: !isHighlightComment(comment),
      });
    },
    [comment, isInConversationSummary, namespace, props],
  );

  return (
    <>
      {maybeRenderReply(
        comment.referencedActivityId,
        hasQuoteComment && RHighlightCommentAnnotationDirty.is(comment.annotation)
          ? comment.annotation
          : undefined,
        referencedComment,
        allAliases,
        isReply,
        hasQuoteComment,
        onReferencedActivityClick,
        isInteractionDisabled,
        props.shouldTruncateAnnotationText,
        isJumpPointerDisabled,
      )}
      {!shouldHideAnnotation &&
        maybeRenderMessageAnnotationSummary(
          comment,
          onReferencedActivityClick,
          isInteractionDisabled,
          props.shouldTruncateAnnotationText,
          props.isReferencingTheSameMessageAsPrevious,
        )}
      {maybeRenderCommentContent(
        comment,
        attachments,
        singleImageAttachment,
        isReply,
        mentionableAliases,
        newMultiAttachmentStyle,
        emojiValidationMap,
        userAlias,
        onNavigateToLink,
        renderOneBigEmoji,
        renderCommentMarkdown,
        isInteractionDisabled,
        textWrapper,
      )}
      {maybeRenderCommentUnfurls(panelId, comment, isInteractionDisabled)}
      {maybeRenderAttachments(
        comment,
        attachments,
        singleImageAttachment,
        newMultiAttachmentStyle,
        isInteractionDisabled,
        isCommentMarginDisabled,
      )}
      {maybeRenderLinkError()}
    </>
  );
});

function maybeRenderCommentUnfurls(
  panelId: NewPanelId,
  comment: CommentModel,
  isInteractionDisabled?: boolean,
) {
  return (
    <CommentViewerUnfurls
      panelId={panelId}
      conversationId={comment.conversationId}
      unfurls={comment.unfurls}
      isInteractionDisabled={isInteractionDisabled}
    />
  );
}

function maybeRenderCommentContent(
  comment: CommentModel,
  attachments: ReadonlyArray<AttachmentModel>,
  singleImageAttachment: AttachmentModel | undefined,
  isReply: boolean,
  mentionableAliases: ReadonlySet<string>,
  newMultiAttachmentStyle: boolean,
  emojiValidationMap: EmojiMap,
  userAlias?: string,
  onLinkClick?: (link: string, shiftKey?: boolean) => void,
  oneBigEmojiRenderer?: RendererOf<ReactNode>,
  commentMarkdownRenderer?: RendererOf<CommentMarkdownRendererProps>,
  isInteractionDisabled?: boolean,
  textWrapper?: (child: ReactNode) => ReactNode,
) {
  // Is comment referencing another activity?
  const isReference = isReply || RAnnotationComment.is(comment);

  // Single Image Strategy - (singleImageAttachment is null-checked in getContentRenderingMethod)
  if (singleImageAttachment && singleImageAttachment.status === AttachmentStatusesEnum.UPLOADED) {
    return renderCommentWithSingleImageStrategy(singleImageAttachment, isReference, isInteractionDisabled);
  }

  // One Big Emoji Strategy
  if (isCommentOnlyAnEmoji(comment, isReply, attachments, emojiValidationMap)) {
    return renderCommentWithOneBigEmojiStrategy(
      comment,
      mentionableAliases,
      emojiValidationMap,
      onLinkClick,
      oneBigEmojiRenderer,
    );
  }

  // Just Attachments Strategy
  if (attachments.length > 0 && !comment.text && !isReply) {
    return null;
  } // Attachments are rendered outside of the main content.

  const maybeRenderTextWrapper = textWrapper === undefined ? (child: ReactNode) => child : textWrapper;

  // Markdown Text Strategy
  return maybeRenderTextWrapper(
    renderCommentWithMarkdownStrategy(
      comment,
      isReply,
      mentionableAliases,
      newMultiAttachmentStyle,
      emojiValidationMap,
      onLinkClick,
      userAlias,
      commentMarkdownRenderer,
    ),
  );
}

function renderCommentWithMarkdownStrategy(
  comment: CommentModel,
  isReply: boolean,
  mentionableAliases: ReadonlySet<string>,
  newMultiAttachmentStyle: boolean,
  emojiValidationMap: EmojiMap,
  onLinkClick?: (link: string, shiftKey?: boolean) => void,
  userAlias?: string,
  commentMarkdownRenderer: RendererOf<CommentMarkdownRendererProps> = defaultCommentMarkdownRenderer,
) {
  return commentMarkdownRenderer({
    newMultiAttachmentStyle,
    isReply,
    markdownNode: (
      <MarkdownFormatter
        text={comment.text}
        transform={makeMarkdownTransforms(comment.unfurls, mentionableAliases, emojiValidationMap, userAlias)}
        onLinkClick={onLinkClick}
        formatter={MarkdownTokenFormatter}
      />
    ),
  });
}

function renderCommentWithSingleImageStrategy(
  singleImageAttachment: UploadedAttachmentModel,
  isReference: boolean,
  isInteractionDisabled?: boolean,
) {
  return (
    <StyledSingleImageDiv $isReference={isReference}>
      <CommentViewerSingleImageAttachmentContainer
        attachment={singleImageAttachment}
        isInteractionDisabled={isInteractionDisabled}
      />
    </StyledSingleImageDiv>
  );
}

function renderCommentWithOneBigEmojiStrategy(
  comment: CommentModel,
  mentionableAliases: ReadonlySet<string>,
  emojiValidationMap: EmojiMap,
  onLinkClick?: (link: string, shiftKey?: boolean) => void,
  oneBigEmojiRenderer: RendererOf<ReactNode> = defaultOneBigEmojiRenderer,
) {
  return oneBigEmojiRenderer(
    <MarkdownFormatter
      text={comment.text}
      transform={makeMarkdownTransforms(comment.unfurls, mentionableAliases, emojiValidationMap)}
      onLinkClick={onLinkClick}
      formatter={MarkdownTokenFormatter}
    />,
  );
}

function maybeRenderReply(
  referencedActivityId: number | undefined,
  referencedCommentAnnotation: AnnotationHighlightCommentModel | undefined,
  referencedComment: CommentModel | undefined,
  allAliases: ReadonlySet<string>,
  isReply: boolean,
  hasQuoteComment: boolean,
  onReferencedActivityClick?: HandlerOf<number>,
  isInteractionDisabled?: boolean,
  shouldTruncateAnnotationText?: boolean,
  isJumpPointerDisabled?: boolean,
) {
  if (!isReply) {
    return null;
  }

  const onClick =
    onReferencedActivityClick && referencedActivityId && !isInteractionDisabled && referencedComment
      ? () => onReferencedActivityClick(referencedActivityId)
      : undefined;

  return (
    <StyledReferencedCommentSummaryButton onClick={onClick}>
      <CommentSummary
        hasQuoteComment={hasQuoteComment}
        comment={referencedComment}
        annotation={referencedCommentAnnotation}
        aliases={allAliases}
        shouldTruncateAnnotationText={shouldTruncateAnnotationText}
        isJumpPointerDisabled={isJumpPointerDisabled}
        shouldShowAttachments
      />
    </StyledReferencedCommentSummaryButton>
  );
}

function maybeRenderMessageAnnotationSummary(
  comment: CommentModel,
  onReferencedActivityClick?: HandlerOf<number>,
  isInteractionDisabled?: boolean,
  shouldTruncateAnnotationText?: boolean,
  isReferencingTheSameMessageAsPrevious?: boolean,
) {
  if (!RHighlightMessageAnnotationComment.is(comment)) {
    return null;
  }

  const {annotation, referencedActivityId} = comment;

  const onClick =
    onReferencedActivityClick && !isInteractionDisabled
      ? () => onReferencedActivityClick(referencedActivityId)
      : undefined;

  return (
    <StyledAnnotationSummaryButton onClick={onClick}>
      {onClick && (
        <StyledReferencedCommentSummaryButtonHover>
          <StyledCommentBodyHoverLabel>
            <Icon name="arrowThickSmall" />
            <Trans id="annotation-jumptomessage">Jump to message</Trans>
          </StyledCommentBodyHoverLabel>
        </StyledReferencedCommentSummaryButtonHover>
      )}
      <AnnotationSummaryContainer
        annotation={annotation}
        referencedActivityId={referencedActivityId}
        shouldTruncateText={shouldTruncateAnnotationText}
        shouldHideRecipient={isReferencingTheSameMessageAsPrevious}
      />
    </StyledAnnotationSummaryButton>
  );
}

function maybeRenderAttachments(
  comment: CommentModel,
  attachments: ReadonlyArray<AttachmentModel>,
  singleImageAttachment: AttachmentModel | undefined,
  newMultiAttachmentStyle: boolean,
  isInteractionDisabled?: boolean,
  isCommentMarginDisabled?: boolean,
) {
  if (singleImageAttachment) {
    return null;
  }

  const [imageAttachments, nonImageAttachments] = partition(
    attachments,
    (attachment) => attachment.visualContentType === AttachmentVisualContentTypesEnum.IMAGE,
  );

  const singleCommentWithoutText = attachments.length === 1 && !comment.text.trim();

  return (
    <>
      {attachments.length > 0 && (
        <CommentViewerAttachments
          attachments={[...imageAttachments, ...nonImageAttachments]}
          newMultiAttachmentStyle={newMultiAttachmentStyle}
          isInteractionDisabled={isInteractionDisabled}
          isAttachmentsBelowText={Boolean(comment.text.trim())}
          isCommentMarginDisabled={isCommentMarginDisabled || singleCommentWithoutText}
        />
      )}
    </>
  );
}

/*
 * Transform Mentions and Emojis in the text.
 */

const makeMarkdownTransforms = memoize()(
  (
      unfurls: ReadonlyArray<CommentUnfurlModel>,
      mentionableAliases: ReadonlySet<string>,
      emojiValidationMap: EmojiMap,
      userAlias?: string,
    ) =>
    (node: ReactNode, previousToken?: Token) => {
      const userMentionableAlias = userAlias ? new Set([userAlias]) : new Set('');
      return flow(
        makeMarkdownTransformMentions(userMentionableAlias, undefined, true),
        makeMarkdownTransformMentions(mentionableAliases),
        makeMarkdownTransformEmojis(emojiValidationMap, previousToken),
        makeMarkdownTransformUnfurls(unfurls),
      )(node);
    },
);

/*
 * Default Renderers.
 */

const defaultOneBigEmojiRenderer: RendererOf<ReactNode> = (emojiNode) => (
  <StyledEmojiDiv>{emojiNode}</StyledEmojiDiv>
);

export interface CommentMarkdownRendererProps {
  isReply: boolean;
  newMultiAttachmentStyle: boolean;
  markdownNode: ReactNode;
}

const defaultCommentMarkdownRenderer: RendererOf<CommentMarkdownRendererProps> = (props) => {
  const {newMultiAttachmentStyle, isReply, markdownNode} = props;
  return (
    <StyledCommentDiv $newMultiAttachmentStyle={newMultiAttachmentStyle} $isReply={isReply}>
      {markdownNode}
    </StyledCommentDiv>
  );
};

export interface ReferencedCommentSummaryRendererProps {
  onClick?: Handler;
  summaryContentNode: ReactNode;
}
